import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import {
  MainLayout,
  Header,
  MainMenu,
  MenuItem,
  Avatar,
  Button,
  Dropdown,
  Paper,
  theme,
  GlobalStyles,
} from './uikit';
import {
  IconPerson,
  IconProducts,
  IconOrders,
  IconLogout,
  IconShifts,
  IconShiftsAll,
  IconShiftsClosed,
  IconShiftsUpcoming,
} from './uikit/components/icons';
import { NotificationsDropdown } from './components/NotificationsDropdown';
import { RequireManagerOrAdmin } from './components/RequireManagerOrAdmin';
import { useCanAccessManagerSections } from './hooks/useCanAccessManagerSections';
import { AuthProvider, useAuth } from './auth';
import { getConfig } from './config';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { OAuthCallback } from './pages/OAuthCallback';
import { ProductsListPage } from './pages/ProductsListPage';
import { ProductPage } from './pages/ProductPage';
import { ProductFormPage } from './pages/ProductFormPage';
import { OrdersListPage } from './pages/OrdersListPage';
import { OrderPage } from './pages/OrderPage';
import { OrderFormPage } from './pages/OrderFormPage';
import { EmployeesListPage } from './pages/EmployeesListPage';
import { ShiftsListPage } from './pages/ShiftsListPage';
import { ShiftPage } from './pages/ShiftPage';
import { ShiftFormPage } from './pages/ShiftFormPage';
import { MenuButton } from './uikit';
import { NetworkOfflineBanner } from './components/NetworkOfflineBanner';
import { IstockMainLogo } from './components/IstockMainLogo';

function DefaultRedirect() {
  const canAccessManagerSections = useCanAccessManagerSections();
  return (
    <Navigate
      to={canAccessManagerSections ? '/employees' : '/shifts/upcoming'}
      replace
    />
  );
}

/** Список /shifts: для руководителя — страница всех смен; для сотрудника — редирект на первый пункт меню. */
function ShiftsIndexRoute() {
  const canAccessManagerSections = useCanAccessManagerSections();
  if (!canAccessManagerSections) {
    return <Navigate to="/shifts/upcoming" replace />;
  }
  return <ShiftsListPage />;
}

function AppShell() {
  const { status, user, login, logout, refreshAuth } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const canAccessManagerSections = useCanAccessManagerSections();

  if (status === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'var(--font-montserrat)',
          fontSize: 14,
          color: theme.colors.text.secondary,
        }}
      >
        Загрузка…
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 16,
          fontFamily: 'var(--font-montserrat)',
        }}
      >
        <p style={{ fontSize: 14, color: theme.colors.text.secondary }}>Для доступа необходимо войти в систему</p>
        <Button variant="filled" color="primary" onClick={() => void login()}>
          Войти через istock.info
        </Button>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 16,
          fontFamily: 'var(--font-montserrat)',
        }}
      >
        <p style={{ fontSize: 14, color: theme.colors.text.secondary, margin: 0 }}>
          Сервис временно недоступен. Попробуйте обновить данные.
        </p>
        <Button variant="filled" color="primary" onClick={() => void refreshAuth()}>
          Повторить
        </Button>
      </div>
    );
  }

  const userDisplayName =
    user?.givenName && user?.familyName
      ? `${user.givenName} ${user.familyName}`
      : user?.givenName ?? user?.familyName ?? user?.userId ?? '';
  const userInitials =
    user?.givenName && user?.familyName
      ? `${user.givenName[0]}${user.familyName[0]}`.toUpperCase()
      : user?.userId
        ? user.userId.slice(0, 2).toUpperCase()
        : '??';

  const headerActions = (
    <>
      <NotificationsDropdown />
      <Dropdown
        trigger={
          <MenuButton size={42} aria-label="Профиль">
            <Avatar size="xxs" letter={userInitials} rounded />
          </MenuButton>
        }
        open={profileOpen}
        onOpenChange={setProfileOpen}
        placement="left-start"
        width="320px"
      >
        <Paper padding={12} shadow={0}>
          <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 14 }}>
            {userDisplayName}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Button
              variant="text"
              color="primary"
              size="s"
              onClick={() => {
                setProfileOpen(false);
                window.open(`${getConfig().istockURL}/settings`, '_blank');
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconPerson /> Профиль
              </span>
            </Button>
            <Button
              variant="text"
              color="primary"
              size="s"
              onClick={() => {
                setProfileOpen(false);
                void logout();
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconLogout /> Выйти
              </span>
            </Button>
          </div>
        </Paper>
      </Dropdown>
    </>
  );

  const menu = (
    <MainMenu>
      {canAccessManagerSections && (
        <>
          <MenuItem to="/employees" icon={<IconPerson />}>
            Сотрудники
          </MenuItem>
          <MenuItem to="/products" icon={<IconProducts />}>
            Изделия
          </MenuItem>
          <MenuItem to="/orders" icon={<IconOrders />}>
            Заказы
          </MenuItem>
        </>
      )}
      {canAccessManagerSections ? (
        <MenuItem to="/shifts" icon={<IconShifts />}>
          Смены
        </MenuItem>
      ) : (
        <>
          <MenuItem to="/shifts/upcoming" icon={<IconShiftsUpcoming />} exact>
            Предстоящие смены
          </MenuItem>
          <MenuItem to="/shifts/closed" icon={<IconShiftsClosed />} exact>
            Закрытые смены
          </MenuItem>
          <MenuItem to="/shifts/all" icon={<IconShiftsAll />} exact>
            Все смены
          </MenuItem>
        </>
      )}
    </MainMenu>
  );

  return (
    <MainLayout
      header={
        <Header logo={<IstockMainLogo />} title="Управление швейным цехом" actions={headerActions} />
      }
      menu={menu}
    >
      <AppContent />
    </MainLayout>
  );
}

function AppContent() {
  return (
    <Routes>
      <Route
        path="/products"
        element={
          <RequireManagerOrAdmin>
            <ProductsListPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/employees"
        element={
          <RequireManagerOrAdmin>
            <EmployeesListPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/orders"
        element={
          <RequireManagerOrAdmin>
            <OrdersListPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/products/new"
        element={
          <RequireManagerOrAdmin>
            <ProductFormPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <RequireManagerOrAdmin>
            <ProductFormPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/products/:id"
        element={
          <RequireManagerOrAdmin>
            <ProductPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/orders/new"
        element={
          <RequireManagerOrAdmin>
            <OrderFormPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/orders/:id/edit"
        element={
          <RequireManagerOrAdmin>
            <OrderFormPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <RequireManagerOrAdmin>
            <OrderPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route
        path="/shifts/new"
        element={
          <RequireManagerOrAdmin>
            <ShiftFormPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route path="/shifts/upcoming" element={<ShiftsListPage employeeListView="upcoming" />} />
      <Route path="/shifts/closed" element={<ShiftsListPage employeeListView="closed" />} />
      <Route path="/shifts/all" element={<ShiftsListPage employeeListView="all" />} />
      <Route path="/shifts" element={<ShiftsIndexRoute />} />
      <Route
        path="/shifts/:id/edit"
        element={
          <RequireManagerOrAdmin>
            <ShiftFormPage />
          </RequireManagerOrAdmin>
        }
      />
      <Route path="/shifts/:id" element={<ShiftPage />} />
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <NetworkOfflineBanner />
      <AppErrorBoundary>
        <AuthProvider>
          <Routes>
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="*" element={<AppShell />} />
          </Routes>
        </AuthProvider>
      </AppErrorBoundary>
    </ThemeProvider>
  );
}

export { App };
